import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  CircularProgress,
  Checkbox,
  MenuItem,
  Select,
  InputLabel,
  LinearProgress,
} from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import SignupApi, { TrialSignupRequest } from "api/SignupApi";
import { useHistory } from "react-router-dom";

type Step = 1 | 2 | 3 | "done";

type FormState = {
  // Step 1
  companyName: string;
  kana: string;
  representativeName: string;
  email: string;
  phone: string;
  password: string;
  passwordConfirm: string;
  // Step 2
  businessType: "delivery" | "visit";
  openingTime: string; // "HH:mm" 開店時間
  closingTime: string; // "HH:mm" 閉店時間 (日付変更時間は閉店+3hで自動算出)
  pointShopFlag: boolean; // false=一括 true=店舗別
  guestCategoryShopFlag: boolean;
  examEnabled: boolean;
  examType: "orderCount" | "shiftCount";
  examCount: number;
  // Step 3
  referralSource: string;
  agreedTerms: boolean;
};

const initialState: FormState = {
  companyName: "",
  kana: "",
  representativeName: "",
  email: "",
  phone: "",
  password: "",
  passwordConfirm: "",
  businessType: "delivery",
  openingTime: "10:00",
  closingTime: "05:00",
  pointShopFlag: false,
  guestCategoryShopFlag: false,
  examEnabled: false,
  examType: "orderCount",
  examCount: 50,
  referralSource: "",
  agreedTerms: false,
};

const REFERRAL_OPTIONS = [
  "Google 検索",
  "知人 / 業界紹介",
  "X (Twitter) / Instagram",
  "営業メール・電話 / 展示会",
  "その他",
];

const Signup = () => {
  const history = useHistory();
  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<FormState>(initialState);
  const [verifyToken, setVerifyToken] = useState<string>("");
  const [smsCode, setSmsCode] = useState<string>("");
  const [smsSent, setSmsSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
  };

  // ---- Step 1 validation & next ----
  const validateStep1 = (): string | null => {
    if (!form.companyName) return "会社名を入力してください";
    if (!form.kana) return "会社名カナを入力してください";
    if (!form.representativeName) return "代表者名を入力してください";
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))
      return "メールアドレスが不正です";
    if (!form.phone.match(/^[0-9+\-\s]{10,16}$/))
      return "電話番号が不正です";
    if (form.password.length < 8) return "パスワードは 8 文字以上";
    if (form.password !== form.passwordConfirm)
      return "パスワード確認が一致しません";
    return null;
  };

  const goStep2 = () => {
    const err = validateStep1();
    if (err) {
      setError(err);
      return;
    }
    setError("");
    setStep(2);
  };

  // ---- Step 2 validation & next ----
  const goStep3 = () => {
    // Step 2 has no required fields that can be invalid
    setError("");
    setStep(3);
  };

  // ---- SMS Send ----
  const sendSmsCode = async () => {
    setLoading(true);
    setError("");
    try {
      // 本番では reCAPTCHA token をここで取得
      const recaptcha = "dev-skip";
      await SignupApi.sendSmsCode(form.phone, recaptcha);
      setSmsSent(true);
    } catch (e: any) {
      setError(e?.message || "SMS 送信に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (smsCode.length !== 6) {
      setError("6桁のコードを入力してください");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await SignupApi.verifySmsCode(form.phone, smsCode);
      setVerifyToken(res.token);
    } catch (e: any) {
      setError(e?.message || "コードが一致しません");
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    if (!form.agreedTerms) {
      setError("利用規約への同意が必要です");
      return;
    }
    if (!verifyToken) {
      setError("SMS 認証を完了してください");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload: TrialSignupRequest = {
        companyName: form.companyName,
        kana: form.kana,
        representativeName: form.representativeName,
        email: form.email,
        phone: form.phone,
        password: form.password,
        businessType: form.businessType,
        openingTime: `${form.openingTime}:00`,
        closingTime: `${form.closingTime}:00`,
        pointShopFlag: form.pointShopFlag,
        guestCategoryShopFlag: form.guestCategoryShopFlag,
        castExaminationType: form.examEnabled ? form.examType : null,
        castExaminationCount: form.examEnabled ? form.examCount : null,
        referralSource: form.referralSource || null,
        verifyToken,
        agreedTerms: form.agreedTerms,
      };
      await SignupApi.createTrialAccount(payload);
      setStep("done");
    } catch (e: any) {
      setError(e?.message || "アカウント作成に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const progressPercent = step === 1 ? 33 : step === 2 ? 66 : 100;

  return (
    <Box
      display="flex"
      justifyContent="center"
      minHeight="calc(100vh - 200px)"
      bgcolor="#f5f7fa"
      py={6}
    >
      <Paper style={{ width: "100%", maxWidth: 720, padding: 32 }}>
        <Typography variant="h4" style={{ fontWeight: "bold", marginBottom: 8 }}>
          新規アカウント登録
        </Typography>
        <Typography variant="body2" color="textSecondary">
          30日間無料でお試しいただけます。カード登録は不要です。
        </Typography>

        {step !== "done" && (
          <Box mt={3}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="caption" color="textSecondary">
                Step {step === 1 ? "1" : step === 2 ? "2" : "3"} / 3
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {step === 1
                  ? "基本情報"
                  : step === 2
                  ? "運用設定"
                  : "確認 + SMS 認証"}
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progressPercent} />
          </Box>
        )}

        {error && (
          <Alert severity="error" style={{ marginTop: 16 }}>
            {error}
          </Alert>
        )}

        {step === 1 && (
          <Box mt={4} display="flex" flexDirection="column" style={{ gap: 16 }}>
            <TextField
              label="会社名・店舗名"
              value={form.companyName}
              onChange={(e) => update("companyName", e.target.value)}
              variant="outlined"
              fullWidth
              required
            />
            <TextField
              label="会社名カナ"
              placeholder="カブシキガイシャタステック"
              value={form.kana}
              onChange={(e) => update("kana", e.target.value)}
              variant="outlined"
              fullWidth
              required
            />
            <TextField
              label="代表者名"
              value={form.representativeName}
              onChange={(e) => update("representativeName", e.target.value)}
              variant="outlined"
              fullWidth
              required
            />
            <TextField
              label="メールアドレス"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              variant="outlined"
              fullWidth
              required
            />
            <TextField
              label="電話番号 (SMS 認証にも使用)"
              placeholder="09012345678"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              variant="outlined"
              fullWidth
              required
            />
            <TextField
              label="パスワード (8文字以上)"
              type="password"
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              variant="outlined"
              fullWidth
              required
            />
            <TextField
              label="パスワード (確認)"
              type="password"
              value={form.passwordConfirm}
              onChange={(e) => update("passwordConfirm", e.target.value)}
              variant="outlined"
              fullWidth
              required
            />
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={goStep2}
              >
                次へ
              </Button>
            </Box>
          </Box>
        )}

        {step === 2 && (
          <Box mt={4} display="flex" flexDirection="column" style={{ gap: 20 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">業態</FormLabel>
              <RadioGroup
                row
                value={form.businessType}
                onChange={(e) =>
                  update("businessType", e.target.value as "delivery" | "visit")
                }
              >
                <FormControlLabel
                  value="delivery"
                  control={<Radio />}
                  label="デリバリー型 (配車あり)"
                />
                <FormControlLabel
                  value="visit"
                  control={<Radio />}
                  label="来店型 (メンエス等)"
                />
              </RadioGroup>
            </FormControl>

            <Box display="flex" style={{ gap: 16 }}>
              <TextField
                label="開店時間"
                type="time"
                value={form.openingTime}
                onChange={(e) => update("openingTime", e.target.value)}
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
              />
              <TextField
                label="閉店時間"
                type="time"
                value={form.closingTime}
                onChange={(e) => update("closingTime", e.target.value)}
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                helperText="1日の区切り（日付変更時間）は閉店3時間後で自動設定されます"
              />
            </Box>

            <FormControl component="fieldset">
              <FormLabel component="legend">ポイント運用</FormLabel>
              <RadioGroup
                row
                value={form.pointShopFlag ? "shop" : "group"}
                onChange={(e) => update("pointShopFlag", e.target.value === "shop")}
              >
                <FormControlLabel
                  value="group"
                  control={<Radio />}
                  label="全店舗で共通利用"
                />
                <FormControlLabel
                  value="shop"
                  control={<Radio />}
                  label="店舗ごとに分けて運用"
                />
              </RadioGroup>
            </FormControl>

            <FormControl component="fieldset">
              <FormLabel component="legend">顧客ランク運用</FormLabel>
              <RadioGroup
                row
                value={form.guestCategoryShopFlag ? "shop" : "group"}
                onChange={(e) =>
                  update("guestCategoryShopFlag", e.target.value === "shop")
                }
              >
                <FormControlLabel
                  value="group"
                  control={<Radio />}
                  label="全店舗で共通"
                />
                <FormControlLabel
                  value="shop"
                  control={<Radio />}
                  label="店舗ごとに分けて運用"
                />
              </RadioGroup>
            </FormControl>

            <FormControl component="fieldset">
              <FormLabel component="legend">
                性病検査期間 自動判定機能
              </FormLabel>
              <RadioGroup
                row
                value={form.examEnabled ? "yes" : "no"}
                onChange={(e) => update("examEnabled", e.target.value === "yes")}
              >
                <FormControlLabel
                  value="no"
                  control={<Radio />}
                  label="利用しない"
                />
                <FormControlLabel
                  value="yes"
                  control={<Radio />}
                  label="利用する"
                />
              </RadioGroup>
            </FormControl>

            {form.examEnabled && (
              <Box display="flex" style={{ gap: 12 }} alignItems="center">
                <FormControl variant="outlined" style={{ flex: 1 }}>
                  <InputLabel>判定方法</InputLabel>
                  <Select
                    value={form.examType}
                    label="判定方法"
                    onChange={(e) =>
                      update(
                        "examType",
                        e.target.value as "orderCount" | "shiftCount"
                      )
                    }
                  >
                    <MenuItem value="orderCount">接客件数</MenuItem>
                    <MenuItem value="shiftCount">出勤日数</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="件数・日数"
                  type="number"
                  value={form.examCount}
                  onChange={(e) =>
                    update("examCount", parseInt(e.target.value) || 0)
                  }
                  variant="outlined"
                  style={{ width: 140 }}
                />
              </Box>
            )}

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button onClick={() => setStep(1)}>戻る</Button>
              <Button variant="contained" color="primary" onClick={goStep3}>
                次へ
              </Button>
            </Box>
          </Box>
        )}

        {step === 3 && (
          <Box mt={4} display="flex" flexDirection="column" style={{ gap: 16 }}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>tasteck をどこで知りましたか?</InputLabel>
              <Select
                value={form.referralSource}
                label="tasteck をどこで知りましたか?"
                onChange={(e) =>
                  update("referralSource", e.target.value as string)
                }
              >
                {REFERRAL_OPTIONS.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box
              p={2}
              bgcolor="#f9fafb"
              border="1px solid #e5e7eb"
              borderRadius={8}
            >
              <Typography variant="subtitle2" gutterBottom>
                SMS 認証
              </Typography>
              {!smsSent ? (
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    登録電話番号 <strong>{form.phone}</strong> に確認コードを送信します。
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    onClick={sendSmsCode}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                  >
                    {loading ? "送信中..." : "SMS 送信"}
                  </Button>
                </Box>
              ) : verifyToken ? (
                <Alert severity="success">✅ SMS 認証が完了しました</Alert>
              ) : (
                <Box display="flex" style={{ gap: 8 }} alignItems="center">
                  <TextField
                    label="6 桁コード"
                    value={smsCode}
                    onChange={(e) => setSmsCode(e.target.value)}
                    variant="outlined"
                    size="small"
                    inputProps={{ maxLength: 6 }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    onClick={verifyCode}
                  >
                    {loading ? "確認中..." : "認証"}
                  </Button>
                </Box>
              )}
            </Box>

            <Box display="flex" alignItems="flex-start">
              <Checkbox
                checked={form.agreedTerms}
                onChange={(e) => update("agreedTerms", e.target.checked)}
                color="primary"
              />
              <Typography variant="body2">
                <a
                  href="https://tasteck.tech/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  利用規約
                </a>{" "}
                と{" "}
                <a
                  href="https://tasteck.tech/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  プライバシーポリシー
                </a>{" "}
                に同意します
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button onClick={() => setStep(2)}>戻る</Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                disabled={loading || !form.agreedTerms || !verifyToken}
                onClick={submit}
                startIcon={loading ? <CircularProgress size={16} /> : null}
              >
                {loading ? "登録中..." : "登録を完了する"}
              </Button>
            </Box>
          </Box>
        )}

        {step === "done" && (
          <Box mt={4}>
            <Alert severity="success">
              <AlertTitle>アカウント登録完了 🎉</AlertTitle>
              ご登録いただいたメールアドレス宛にログイン情報を送信しました。
              30 日間無料でライトプランの全機能をお試しいただけます。
            </Alert>
            <Box mt={4} textAlign="center">
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => history.push("/login")}
              >
                ログイン画面へ
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Signup;
